const TREATMENT_NAME_TOKEN = /\{{1,2}\s*(TREATMENT_NAME)\s*\}{1,2}/gi;
const TREATMENT_TOKEN = /\{{1,2}\s*(TREATMENT)\s*\}{1,2}/gi;
const CITY_TOKEN = /\{{1,2}\s*(CITY)\s*\}{1,2}/gi;

type Casing = "upper" | "lower" | "title";

/** Detect casing from how the user wrote the placeholder word (e.g. TREATMENT vs Treatment vs treatment). */
function detectCasing(word: string): Casing {
  // if (word === word.toUpperCase()) return "lower";
  // if (word === word.toLowerCase()) return "lower";
  return "title";
}

/** Apply the detected casing to the (already title-cased) replacement value. */
function applyCasing(value: string, casing: Casing): string {
  if (!value) return value;
  switch (casing) {
    case "upper":
      return value.toUpperCase();
    case "lower":
      return value.toLowerCase();
    case "title":
      return value
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    default:
      return value;
  }
}

/**
 * Cleans up whitespace and stray separators left behind after a placeholder is removed,
 * e.g. "Acne in  | Clinic" → "Acne in | Clinic", "Acne |  Clinic" → "Acne | Clinic",
 * and trims dangling " in " / " at " / leading-trailing separators.
 */
function tidyEmpties(text: string): string {
  return text
    // collapse multiple spaces
    .replace(/[ \t]{2,}/g, " ")
    // remove space before punctuation that was orphaned by a missing token
    .replace(/\s+([,;:|–-])/g, "$1")
    // collapse separators that now sit next to each other (e.g. "|  |", "in  |")
    .replace(/([|–-])\s*[|–-]/g, "$1")
    // drop leading/trailing separators left over after removal
    .replace(/^\s*[|,–-]\s*/, "")
    .replace(/\s*[|,–-]\s*$/, "")
    // remove dangling connector words ("in", "at", "for") immediately before a separator/end
    .replace(/\s+\b(in|at|for)\b\s*(?=[|,–-]|$)/gi, "")
    .trim();
}

/**
 * Replaces template placeholders in text with the provided values.
 * Supports {{TREATMENT}}, {{TREATMENT_NAME}} and {{CITY}}. Tolerates 1–2 braces on either side
 * (e.g. `{CITY}}` or `{{Treatment}`) and adopts the placeholder's casing:
 *   {{TREATMENT}} / {{TREATMENT_NAME}} → UPPERCASE
 *   {{Treatment}} / {{Treatment_Name}} → value as-is
 *   {{treatment}} / {{treatment_name}} → lowercase
 *
 * Values are NOT auto title-cased; callers are responsible for passing display-ready
 * strings (e.g. "Laser Hair Removal" rather than "laser-hair-removal").
 *
 * When a value is missing, the corresponding placeholder is REMOVED (replaced with "")
 * and surrounding whitespace / stray separators are tidied up.
 *
 * @param text - The template string (e.g., "{{Treatment}} Packages at Clinic Dermatech {{CITY}}")
 * @param treatmentName - Value for {{TREATMENT}} / {{TREATMENT_NAME}}. Falsy → placeholder is removed.
 * @param city - Value for {{CITY}}. Falsy → placeholder is removed.
 */
export function replaceTemplatePlaceholders(
  text: string,
  treatmentName?: string | null,
  city?: string | null
): string {
  if (!text) return text;
  const baseTreatment = treatmentName ?? "";
  const baseCity = city ?? "";
  const replaced = text
    // {{TREATMENT_NAME}} first so its trailing "_NAME" isn't left behind by {{TREATMENT}} matching.
    .replace(TREATMENT_NAME_TOKEN, (_m, word: string) =>
      applyCasing(baseTreatment, detectCasing(word))
    )
    .replace(TREATMENT_TOKEN, (_m, word: string) =>
      applyCasing(baseTreatment, detectCasing(word))
    )
    .replace(CITY_TOKEN, (_m, word: string) =>
      applyCasing(baseCity, detectCasing(word))
    );
  return tidyEmpties(replaced);
}
